import { useEffect, useState } from 'react';

export interface CompanyOpeningHours {
  weekdayOffice: string;
  weekdayEmergency: string;
  weekendEmergency: string;
}

export interface CompanyLegalSettings {
  legalEntityName: string;
  tradingName: string;
  companyNumber: string;
  cqcProviderId: string;
  icoRegistration: string;
  registeredManager: string;
  nominatedIndividual: string;
  dataProtectionLead: string;
  vatNumber: string;
}

export interface CompanyDocumentAutomation {
  termsAndConditions: boolean;
  privacyPolicy: boolean;
  dataProcessingAgreement: boolean;
  cookiePolicy: boolean;
  dpia: boolean;
  ropa: boolean;
  backupRestorePolicy: boolean;
  incidentResponsePolicy: boolean;
  processorContracts: boolean;
  cqcEvidencePack: boolean;
  employmentContract: boolean;
  clientContract: boolean;
  payrollDocuments: boolean;
}

export interface CompanyProfile {
  brandName: string;
  publicName: string;
  tagline: string;
  addressLine1: string;
  addressLine2: string;
  townCity: string;
  county: string;
  postcode: string;
  country: string;
  phone: string;
  onCallPhone: string;
  email: string;
  website: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoFullUrl: string;
  logoMarkUrl: string;
  openingHours: CompanyOpeningHours;
  legal: CompanyLegalSettings;
  documentAutomation: CompanyDocumentAutomation;
  updatedAt: string;
}

export const COMPANY_PROFILE_STORAGE_KEY = 'fuchsia:white-label:company-profile';
export const COMPANY_DOCUMENT_VARIABLES_KEY = 'fuchsia:white-label:document-variables';
export const COMPANY_PROFILE_EVENT = 'fuchsia-company-profile-updated';

export const defaultCompanyProfile: CompanyProfile = {
  brandName: 'FUCHSIA HOME CARE CATALIN',
  publicName: 'Fuchsia Homecare Gorleston',
  tagline: 'Always Caring, Always Here',
  addressLine1: 'Yarmouth Business Park, Suffolk Rd',
  addressLine2: '',
  townCity: 'Great Yarmouth',
  county: 'Norfolk',
  postcode: 'NR31 0LN',
  country: 'United Kingdom',
  phone: '01493738488',
  onCallPhone: '07443145811',
  email: 'lowestoft@fuchsiahomecare.co.uk',
  website: 'https://fuchsiagorleston.com/',
  primaryColor: '#6c3a80',
  secondaryColor: '#25334a',
  accentColor: '#b58ac8',
  logoFullUrl: '/images/fuchsia-logo-full.png',
  logoMarkUrl: '/images/fuchsia-logo-mark.png',
  openingHours: {
    weekdayOffice: 'Monday - Friday - 8:30 - 17:00',
    weekdayEmergency: 'Monday to Friday - 17:00 - 8:30 - Emergency Only Contact',
    weekendEmergency: 'Saturday - Sunday - Emergency Only Contact',
  },
  legal: {
    legalEntityName: 'Fuchsia Homecare Gorleston',
    tradingName: 'Fuchsia Homecare Gorleston',
    companyNumber: '',
    cqcProviderId: '',
    icoRegistration: '',
    registeredManager: '',
    nominatedIndividual: '',
    dataProtectionLead: '',
    vatNumber: '',
  },
  documentAutomation: {
    termsAndConditions: true,
    privacyPolicy: true,
    dataProcessingAgreement: true,
    cookiePolicy: true,
    dpia: true,
    ropa: true,
    backupRestorePolicy: true,
    incidentResponsePolicy: true,
    processorContracts: true,
    cqcEvidencePack: true,
    employmentContract: true,
    clientContract: true,
    payrollDocuments: true,
  },
  updatedAt: '2026-05-23T00:00:00.000Z',
};

export function formatCompanyAddress(profile: CompanyProfile): string {
  return [
    profile.addressLine1,
    profile.addressLine2,
    profile.townCity,
    profile.county,
    profile.postcode,
  ].filter(Boolean).join(', ');
}

export function loadCompanyProfile(): CompanyProfile {
  if (typeof window === 'undefined') return defaultCompanyProfile;
  try {
    const raw = window.localStorage.getItem(COMPANY_PROFILE_STORAGE_KEY);
    if (!raw) return defaultCompanyProfile;
    const parsed = JSON.parse(raw) as Partial<CompanyProfile>;
    return {
      ...defaultCompanyProfile,
      ...parsed,
      openingHours: {
        ...defaultCompanyProfile.openingHours,
        ...parsed.openingHours,
      },
      legal: {
        ...defaultCompanyProfile.legal,
        ...parsed.legal,
      },
      documentAutomation: {
        ...defaultCompanyProfile.documentAutomation,
        ...parsed.documentAutomation,
      },
    };
  } catch {
    return defaultCompanyProfile;
  }
}

export function saveCompanyProfile(profile: CompanyProfile) {
  const nextProfile = { ...profile, updatedAt: new Date().toISOString() };
  window.localStorage.setItem(COMPANY_PROFILE_STORAGE_KEY, JSON.stringify(nextProfile));
  window.localStorage.setItem(
    COMPANY_DOCUMENT_VARIABLES_KEY,
    JSON.stringify({
      companyName: nextProfile.publicName,
      brandName: nextProfile.brandName,
      legalEntityName: nextProfile.legal.legalEntityName,
      tradingName: nextProfile.legal.tradingName,
      companyNumber: nextProfile.legal.companyNumber,
      cqcProviderId: nextProfile.legal.cqcProviderId,
      icoRegistration: nextProfile.legal.icoRegistration,
      vatNumber: nextProfile.legal.vatNumber,
      registeredManager: nextProfile.legal.registeredManager,
      nominatedIndividual: nextProfile.legal.nominatedIndividual,
      dataProtectionLead: nextProfile.legal.dataProtectionLead,
      address: formatCompanyAddress(nextProfile),
      phone: nextProfile.phone,
      onCallPhone: nextProfile.onCallPhone,
      email: nextProfile.email,
      website: nextProfile.website,
      logoFullUrl: nextProfile.logoFullUrl,
      logoMarkUrl: nextProfile.logoMarkUrl,
      openingHours: nextProfile.openingHours,
      updatedAt: nextProfile.updatedAt,
    })
  );
  window.dispatchEvent(new CustomEvent(COMPANY_PROFILE_EVENT, { detail: nextProfile }));
}

export function useCompanyProfile() {
  const [profile, setProfile] = useState<CompanyProfile>(() => loadCompanyProfile());

  useEffect(() => {
    const refresh = () => setProfile(loadCompanyProfile());
    window.addEventListener(COMPANY_PROFILE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(COMPANY_PROFILE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return profile;
}
